import { useTranslation } from 'react-i18next'
import { UserRoundX } from 'lucide-react'

import { UserAvatar } from '@/components/UserAvatar/UserAvatar'
import { User, UserStatus } from '@/schema'
import { cn, formatFullName } from '@/utils/common'

// Extended interface to handle UserDetail or slightly different user shapes
interface DisplayUser {
  first_name?: string | null
  last_name?: string | null
  avatar_url?: string | null
  status?: UserStatus | string | null
  email?: string | null
}

interface UserDisplayProps {
  user?: DisplayUser | null
  showAvatar?: boolean
  showName?: boolean
  className?: string
  avatarSize?: string
  textClassName?: string
  avatarBadge?: React.ReactNode
}

export const UserDisplay = ({
  user,
  showAvatar = true,
  showName = true,
  className,
  avatarSize,
  textClassName,
  avatarBadge,
}: UserDisplayProps) => {
  const { t } = useTranslation()

  if (!user) {
    if (!showName) return null
    return <span className={cn('text-muted-foreground', textClassName)}>-</span>
  }

  const isDisabled = user.status === 'disabled'

  if (isDisabled) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {showAvatar && (
          <div className="relative flex-shrink-0">
            <div
              className={cn(
                'flex items-center justify-center bg-muted rounded-full',
                avatarSize || 'size-8'
              )}
            >
              <UserRoundX className="size-4 text-muted-foreground" />
            </div>
            {avatarBadge}
          </div>
        )}
        {showName && (
          <span className={cn('text-muted-foreground italic', textClassName)}>
            {t('common.deletedUser')}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showAvatar && (
        <div className="relative flex-shrink-0">
          <UserAvatar
            user={user as User}
            className={avatarSize}
            size={avatarSize}
          />
          {avatarBadge}
        </div>
      )}

      {showName && (
        <span className={textClassName}>
          {formatFullName(user.first_name || '', user.last_name || '') || '-'}
        </span>
      )}
    </div>
  )
}
